import React, { useEffect, useState } from 'react';
import { CheckCircle, Sparkles, ArrowLeft } from 'lucide-react';
import analytics from '../utils/analytics';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { API_BASE_URL } from '../apiConfig';
import { apiFetch } from '../api/authHeaders';
import { Alert } from './ui';

const SuccessPage = () => {
  const [paymentStatus, setPaymentStatus] = useState('verifying');
  const [sessionDetails, setSessionDetails] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isSubscription, setIsSubscription] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(7);
  const [couponCode, setCouponCode] = useState(null);
  const [verifyingStartTime] = useState(Date.now()); // Track when verification started
  const [needsFirebaseAuth, setNeedsFirebaseAuth] = useState(false);

  // Check Firebase auth state after subscription verification
  useEffect(() => {
    if (isSubscription) {
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        if (!firebaseUser) {
          // User purchased subscription but isn't signed in with Firebase
          setNeedsFirebaseAuth(true);
        } else {
          // User is signed in, sync with backend
          try {
            const userData = {
              id: firebaseUser.uid,
              email: firebaseUser.email,
              name: firebaseUser.displayName,
              picture: firebaseUser.photoURL,
              provider: 'firebase-google'
            };
            
            await apiFetch(`${API_BASE_URL}/api/auth/sync-user`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(userData)
            });
            
            setNeedsFirebaseAuth(false);
          } catch (error) {
            console.error('Failed to sync user after subscription:', error);
          }
        }
      });
      
      return () => unsubscribe();
    }
  }, [isSubscription]);

  useEffect(() => {
    // Get session ID from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const sessionIdParam = urlParams.get('session_id');

    if (sessionIdParam) {
      // First check the session status
      checkPaymentStatus(sessionIdParam);
      // Then verify payment with backend
      verifyPayment(sessionIdParam);
    } else {
      setPaymentStatus('no_session');
    }
  }, []);

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/debug-payment/${sessionId}`);
      const result = await response.json();
      
      setSessionDetails(result);
      
      // Don't set to 'paid' here - let verifyPayment handle it with proper countdown
      // This is just for initial status display
      if (result.paymentStatus === 'unpaid') {
        setPaymentStatus('unpaid');
      } else if (result.paymentStatus !== 'paid') {
        // Keep as 'verifying' if not clearly unpaid
        // verifyPayment will handle setting to 'success' or 'paid' with countdown
      }
    } catch (error) {
      console.error('Failed to check payment status:', error);
      // Don't set to error here - let verifyPayment handle errors
    }
  };

  const verifyPayment = async (sessionId) => {
    try {
      console.log('Verifying payment for session:', sessionId);
      
      // First, check if it's a subscription by trying to retrieve the session
      // We'll try subscription verification first, then fall back to one-time payment
      let response = await apiFetch(`${API_BASE_URL}/api/verify-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId }),
      });

      let result;
      try {
        result = await response.json();
      } catch (error) {
        console.error('Failed to parse subscription verification response:', error);
        result = {};
      }
      console.log('Subscription verification response:', result);
      console.log('Subscription verification response status:', response.status);

      // Check if response indicates it's NOT a subscription (specific error message)
      const isNotSubscription = result.error && (
        result.error.includes('not a subscription') || 
        result.error.includes('Session is not a subscription') ||
        result.error.includes('SUBSCRIPTION_NOT_FOUND')
      );

      // Check if this is a subscription - look for subscription indicators
      // The server returns { success: true, subscriptionId, customerId, status, currentPeriodEnd, analysesLimit, analysesUsed }
      const hasSubscriptionData = !isNotSubscription && (
        result.success === true ||
        (result.subscriptionId && response.status === 200) ||
        (result.analysesLimit === 50 && response.status === 200)
      );

      if (hasSubscriptionData) {
        console.log('✅ Detected as subscription payment - will show subscription message');
        console.log('Subscription data:', { success: result.success, subscriptionId: result.subscriptionId, analysesLimit: result.analysesLimit });
        // Subscription payment verified
        // Ensure minimum display time for verifying screen (2 seconds)
        const elapsed = Date.now() - verifyingStartTime;
        const minDisplayTime = 2000; // 2 seconds minimum
        const waitTime = Math.max(0, minDisplayTime - elapsed);
        
        setTimeout(() => {
          console.log('Setting subscription state: isSubscription=true, quantity=50');
          setPaymentStatus('success');
          setQuantity(50); // 50 analyses per month
          setIsSubscription(true); // Mark as subscription - THIS IS CRITICAL
          localStorage.setItem('criticai_subscription', 'true');
          localStorage.setItem('criticai_subscription_id', result.subscriptionId || result.subscription?.id);
          if (result.customerId || result.subscription?.customer) {
            localStorage.setItem('criticai_subscription_customer_id', result.customerId || result.subscription?.customer);
          }
          localStorage.setItem('criticai_subscription_date', new Date().toISOString());
          localStorage.setItem('criticai_subscription_period_end', result.currentPeriodEnd || result.subscription?.currentPeriodEnd);
          
          // Track subscription purchase event in Google Analytics
          analytics.trackSubscription(
            result.subscriptionId || result.subscription?.id, // subscription ID
            sessionId, // session/transaction ID
            9.00, // value
            'USD', // currency
            'CriticAI Monthly Subscription' // plan name
          );
          
          console.log('Subscription verified successfully:', result);
          console.log('Starting 7 second redirect countdown...');
          
          // Start countdown and redirect to main page after successful payment (7 seconds)
          setRedirectCountdown(7);
          const countdownInterval = setInterval(() => {
            setRedirectCountdown((prev) => {
              console.log('Countdown:', prev);
              if (prev <= 1) {
                console.log('Redirecting to main page...');
                clearInterval(countdownInterval);
                window.location.href = '/';
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        }, waitTime);
        return;
      }

      // One-time credits: same browser session as checkout (cross-origin cookie). Retry when sub endpoint errors (e.g. 500).
      // Also treat 401 as "needs auth for subscription" — do not hard-fail; show sign-in prompt instead.
      const tryOneTimePayment =
        isNotSubscription || (response.status >= 500 && response.status < 600);

      if (response.status === 401) {
        console.warn('Subscription verification requires sign-in:', result);
        localStorage.setItem('criticai_subscription', 'true');
        setIsSubscription(true);
        setNeedsFirebaseAuth(true);
        setQuantity(result.analysesLimit || 50);
        if (result.subscriptionId) {
          localStorage.setItem('criticai_subscription_id', result.subscriptionId);
        }
        setPaymentStatus('success');
        setRedirectCountdown(7);
        const countdownInterval = setInterval(() => {
          setRedirectCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(countdownInterval);
              window.location.href = '/';
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        return;
      }

      if (tryOneTimePayment) {
        console.log('Trying one-time payment verification...');
        response = await apiFetch(`${API_BASE_URL}/api/verify-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId }),
      });

      result = await response.json();
      console.log('Payment verification response:', result);

      if (result.success) {
        // Mark user as paid in session
        // Ensure minimum display time for verifying screen (2 seconds)
        const elapsed = Date.now() - verifyingStartTime;
        const minDisplayTime = 2000; // 2 seconds minimum
        const waitTime = Math.max(0, minDisplayTime - elapsed);
        
        setTimeout(() => {
          const purchasedQuantity = result.quantity || 1;
          setQuantity(purchasedQuantity);
          setCouponCode(result.couponCode);
          localStorage.setItem('criticai_paid', 'true');
          localStorage.setItem('criticai_payment_date', new Date().toISOString());
          localStorage.setItem('criticai_paid_analyses', result.paidAnalyses.toString());
          const prevHw = parseInt(
            localStorage.getItem('criticai_paid_analyses_high_water') || '0',
            10
          );
          const nextHw = Math.max(prevHw || 0, result.paidAnalyses || 0);
          localStorage.setItem('criticai_paid_analyses_high_water', String(nextHw));
          
          // Track purchase event in Google Analytics
          const credits = purchasedQuantity;
          const valueUsd = Math.round(credits * 100) / 100;
          analytics.trackPurchase(
            sessionId, // transaction ID
            valueUsd, // pre-discount list value: $1 per credit
            'USD', // currency
            [{
              item_id: result.tier || 'pay_per_use',
              item_name: `CriticAI — ${credits} credit${credits !== 1 ? 's' : ''} ($1 USD each)`,
              category: 'Music Analysis',
              quantity: credits,
              price: 1.0
            }],
            result.couponCode // coupon code
          );
          
          console.log('Payment verified successfully:', result);
          console.log('Starting 7 second redirect countdown...');
          
          setPaymentStatus('success');
          setRedirectCountdown(7);
          const countdownInterval = setInterval(() => {
            setRedirectCountdown((prev) => {
              console.log('Countdown:', prev);
              if (prev <= 1) {
                console.log('Redirecting to main page...');
                clearInterval(countdownInterval);
                window.location.href = '/';
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        }, waitTime);
      } else {
        console.error('Payment verification failed:', result);
        // Try to debug the session
        try {
          const debugResponse = await apiFetch(`${API_BASE_URL}/api/debug-payment/${sessionId}`);
          const debugResult = await debugResponse.json();
          console.log('Payment session debug info:', debugResult);
          
          // If the session shows as paid but verification failed, still redirect
          if (debugResult.paymentStatus === 'paid') {
            console.log('Session shows as paid, proceeding with redirect despite verification failure');
            // Ensure minimum display time for verifying screen (2 seconds)
            const elapsed = Date.now() - verifyingStartTime;
            const minDisplayTime = 2000; // 2 seconds minimum
            const waitTime = Math.max(0, minDisplayTime - elapsed);
            
            setTimeout(() => {
              setPaymentStatus('paid');
              setQuantity(1); // Default quantity
              setIsSubscription(false); // Fallback is not a subscription
              
              setRedirectCountdown(7);
              const countdownInterval = setInterval(() => {
                setRedirectCountdown((prev) => {
                  console.log('Fallback countdown:', prev);
                  if (prev <= 1) {
                    console.log('Fallback redirecting to main page...');
                    clearInterval(countdownInterval);
                    window.location.href = '/';
                    return 0;
                  }
                  return prev - 1;
                });
              }, 1000);
            }, waitTime);
          }
        } catch (debugError) {
          console.error('Failed to get debug info:', debugError);
        }
      }
      } else {
        // Subscription verification didn't explicitly say "not a subscription"
        // This might be a subscription that failed verification for another reason
        // Don't fall through to one-time payment - show error instead
        console.error('Subscription verification failed but may still be a subscription:', result);
        setPaymentStatus('error');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
    }
  };

  const handleBackToApp = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.05)_0%,transparent_50%)]"></div>
      
      <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
        <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8 max-w-md w-full text-center animate-slide-up">
          {paymentStatus === 'verifying' && (
            <>
              <div className="relative inline-block mb-6">
                <div className="animate-spin rounded-full h-20 w-20 border-b-2 border-white mx-auto"></div>
              </div>
              <h1 className="text-3xl font-bold text-gradient mb-4">Verifying Payment...</h1>
              <p className="text-critic-text-muted mb-6 font-normal">
                Please wait while we verify your payment. This may take a few moments.
              </p>
            </>
          )}
          
          {paymentStatus === 'success' && (
            <>
              <div className="relative inline-block mb-6">
                <CheckCircle className="h-20 w-20 text-green-500 mx-auto" />
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-black" />
                </div>
              </div>
              <h1 className="text-3xl font-bold text-gradient mb-4">Payment Successful!</h1>
              <p className="text-critic-text-muted mb-6 font-normal">
                {isSubscription 
                  ? `Thank you for subscribing! You now have access to ${quantity} creative analyses per month.`
                  : `Thank you for your purchase! You now have ${quantity} additional creative analysis${quantity > 1 ? 'es' : ''} available.`
                }
              </p>
              {isSubscription && needsFirebaseAuth && (
                <Alert tone="warning" title="⚠️ Sign In Required" className="mb-6">
                  Please sign in with Google to activate your subscription and access your analyses across all devices.
                </Alert>
              )}
              {/* Debug info - remove in production */}
              {process.env.NODE_ENV === 'development' && (
                <p className="text-xs text-critic-text-muted mt-2 font-normal">
                  Debug: isSubscription={isSubscription.toString()}, quantity={quantity}
                </p>
              )}
              
              <div className="bg-gray-800 rounded-lg p-4 mb-6 border border-gray-700">
                <h3 className="text-lg font-semibold text-white mb-2">What's Next?</h3>
                <ul className="text-sm text-critic-text-secondary space-y-1 text-left font-normal">
                  <li>• Upload your song for analysis</li>
                  <li>• Get detailed AI-powered review</li>
                  <li>• Access technical scoring and insights</li>
                  <li>• Discover similar artists</li>
                </ul>
              </div>
              
              <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                  <p className="text-blue-400 font-medium">Redirecting to main page...</p>
                </div>
                <p className="text-blue-300 text-sm">
                  You'll be redirected in <span className="font-bold text-blue-200">{redirectCountdown}</span> second{redirectCountdown !== 1 ? 's' : ''}
                </p>
              </div>
            </>
          )}
          
          {paymentStatus === 'paid' && (
            <>
              <div className="relative inline-block mb-6">
                <CheckCircle className="h-20 w-20 text-green-500 mx-auto" />
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-black" />
                </div>
              </div>
              <h1 className="text-3xl font-bold text-gradient mb-4">
                {['5FEEDBACK', '10FEEDBACK', '20FEEDBACK'].includes(couponCode) ? 'Coupon Applied Successfully!' : 'Payment Successful!'}
              </h1>
              <p className="text-critic-text-muted mb-6 font-normal">
                {['5FEEDBACK', '10FEEDBACK', '20FEEDBACK'].includes(couponCode) 
                  ? `Thank you for your feedback! You now have ${quantity} additional creative analysis${quantity > 1 ? 'es' : ''} available.`
                  : `Thank you for your purchase! You now have ${quantity} additional creative analysis${quantity > 1 ? 'es' : ''} available.`
                }
              </p>
              
              {['5FEEDBACK', '10FEEDBACK', '20FEEDBACK'].includes(couponCode) && (
                <div className="bg-green-900/20 border border-green-700/50 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <CheckCircle className="h-5 w-5 text-green-400" />
                    <p className="text-green-400 font-medium">{couponCode} Coupon Applied</p>
                  </div>
                  <p className="text-green-300 text-sm">
                    You received {quantity} analysis credit{quantity !== 1 ? 's' : ''} (pay-per-use at $1 USD each) for providing detailed feedback!
                  </p>
                </div>
              )}
              
              <div className="bg-gray-800 rounded-lg p-4 mb-6 border border-gray-700">
                <h3 className="text-lg font-semibold text-white mb-2">What's Next?</h3>
                <ul className="text-sm text-critic-text-secondary space-y-1 text-left font-normal">
                  <li>• Upload your song for analysis</li>
                  <li>• Get detailed AI-powered review</li>
                  <li>• Access technical scoring and insights</li>
                  <li>• Discover similar artists</li>
                </ul>
              </div>
              
              <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                  <p className="text-blue-400 font-medium">Redirecting to main page...</p>
                </div>
                <p className="text-blue-300 text-sm">
                  You'll be redirected in <span className="font-bold text-blue-200">{redirectCountdown}</span> second{redirectCountdown !== 1 ? 's' : ''}
                </p>
              </div>
            </>
          )}
          
          {paymentStatus === 'unpaid' && (
            <>
              <div className="relative inline-block mb-6">
                <div className="h-20 w-20 text-yellow-500 mx-auto border-4 border-yellow-500 rounded-full flex items-center justify-center">
                  <span className="text-2xl">⚠️</span>
                </div>
              </div>
              <h1 className="text-3xl font-bold text-gradient mb-4">Payment Not Completed</h1>
              <p className="text-critic-text-muted mb-6 font-normal">
                It looks like your payment wasn't completed. Please try again or{' '}
                <a href="mailto:hello@criticai.ca" className="text-blue-400 hover:text-blue-300 underline">
                  contact support
                </a>{' '}
                if you believe this is an error.
              </p>
              
              <Alert tone="warning" title="Payment Status" className="mb-6">
                Session Status: {sessionDetails?.status || 'Unknown'}
                <br />
                Payment Status: {sessionDetails?.paymentStatus || 'Unknown'}
              </Alert>
            </>
          )}
          
          {paymentStatus === 'error' && (
            <>
              <div className="relative inline-block mb-6">
                <div className="h-20 w-20 text-red-500 mx-auto border-4 border-red-500 rounded-full flex items-center justify-center">
                  <span className="text-2xl">❌</span>
                </div>
              </div>
              <h1 className="text-3xl font-bold text-gradient mb-4">Payment Error</h1>
              <p className="text-critic-text-muted mb-6 font-normal">
                There was an error processing your payment. Please try again or{' '}
                <a href="mailto:hello@criticai.ca" className="text-blue-400 hover:text-blue-300 underline">
                  contact support
                </a>.
              </p>
            </>
          )}
          
          <button
            onClick={handleBackToApp}
            className="btn-primary w-full flex items-center justify-center space-x-2"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to CriticAI</span>
          </button>
          
          {paymentStatus === 'paid' && (
            <p className="text-xs text-critic-text-muted mt-4 font-normal">
              A receipt has been sent to your email address.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuccessPage; 